import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  Divider,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  AddCircleRounded as AddCircleRoundedIcon,
  CancelRounded as CancelRoundedIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DocumentAutocomplete } from "./DocumentAutocomplete";
import { useNewDocument } from "../../../hooks/panelUsers/documentsActions/useNewDocument";
import { useDeleteSection } from "../../../hooks/panelUsers/documentsActions/useDeleteSection";
import { useAddSections } from "../../../hooks/panelUsers/documentsActions/useAddSections";
import { useDeleteDocumentUser } from "../../../hooks/panelUsers/documentsActions/useDeleteDocumentUser";
import { toastAlert } from "../../../utils/AlertContext";
import { useFetchSections } from "../../../hooks/useGetSections";

export const ModalDocumentUser = ({
  open,
  onClose,
  selectedUser,
  token,
  refresh,
}) => {
  const [documentSelected, setDocumentSelected] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [expandedNewDoc, setExpandedNewDoc] = useState(null);
  const [sectionsSelected, setSectionsSelected] = useState([]);
  const [selectedSectionsGroup, setSelectedSectionsGroup] = useState(null);
  const [loading, setLoading] = useState(false);

  const validDocumentId = documentSelected?.id ? documentSelected.id : null;
  const { sections } = useFetchSections(token, validDocumentId);
  const { addDocument } = useNewDocument(token);
  const { deleteSections } = useDeleteSection(token);
  const { addSections } = useAddSections(token);
  const { deleteDocument } = useDeleteDocumentUser(token);
  useEffect(() => {
    if (!open) {
      setDocuments([]);
      setExpanded(null);
      setSectionsSelected([]);
      setSelectedSectionsGroup(null);
      setDocumentSelected(null);
      setExpandedNewDoc(null);
    }
  }, [open]);

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
    setSectionsSelected([]);
    setSelectedSectionsGroup(null);
  };

  const getAssignedSections = useCallback(() => {
    if (!expanded || !selectedUser?.permissions) return [];
    const permission = selectedUser.permissions.find(
      (p) => p.ID_DOCUMENTO === expanded
    );
    return permission?.sections?.assigned || [];
  }, [expanded, selectedUser?.permissions]);

  const getAvailableSections = useCallback(() => {
    if (!expanded || !selectedUser?.permissions) return [];
    const permission = selectedUser.permissions.find(
      (p) => p.ID_DOCUMENTO === expanded
    );
    return permission?.sections?.available || [];
  }, [expanded, selectedUser?.permissions]);

  useEffect(() => {
    if (documentSelected && documentSelected.id && sections.length > 0) {
      setDocuments((prev) => [
        ...prev,
        {
          document_id: documentSelected.id,
          name: documentSelected.label,
          sections: sections.map((section) => ({
            id: section.ID_SECCION,
            name: section.NOMBRE_SECCION,
            active: false,
          })),
        },
      ]);
      setDocumentSelected(null);
    }
  }, [documentSelected, sections]);

  const toggleSection = (docId, sectionId) => {
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.document_id === docId
          ? {
              ...doc,
              sections: doc.sections.map((s) =>
                s.id === sectionId ? { ...s, active: !s.active } : s
              ),
            }
          : doc
      )
    );
  };

  const executeRefresh = useCallback(async () => {
    if (refresh && typeof refresh === "function") {
      return refresh();
    }
    return Promise.resolve();
  }, [refresh]);

  const handleSave = async () => {
    const documentsWithoutSections = documents.filter(
      (doc) => doc.sections.filter((s) => s.active).length === 0
    );

    if (documentsWithoutSections.length > 0) {
      const documentNames = documentsWithoutSections
        .map((doc) => doc.name)
        .join(", ");
      toastAlert(
        `Los siguientes documentos no tienen secciones seleccionadas: ${documentNames}`,
        "warning"
      );
      return;
    }

    const formattedDocs = documents
      .map((doc) => ({
        document_id: doc.document_id,
        section_id: doc.sections.filter((s) => s.active).map((s) => s.id),
      }))
      .filter((doc) => doc.section_id.length > 0);

    if (!formattedDocs.length) {
      toastAlert(
        "Debes seleccionar al menos una sección por documento",
        "warning"
      );
      return;
    }

    setLoading(true);
    try {
      await addDocument(formattedDocs, selectedUser.id);
      await executeRefresh();
      toastAlert("Documentos asignados con éxito", "success");
      onClose();
    } catch (error) {
      toastAlert("Error al guardar los documentos", "error");
    } finally {
      setLoading(false);
    }
  };
  const toggleSelection = (sectionId, isAssigned) => {
    const sameGroup =
      !sectionsSelected.length || selectedSectionsGroup === isAssigned;
    if (!sameGroup) return;

    setSectionsSelected((prev) => {
      const newSelected = prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId];

      if (newSelected.length === 0) {
        setSelectedSectionsGroup(null);
      } else if (prev.length === 0) {
        setSelectedSectionsGroup(isAssigned);
      }

      return newSelected;
    });
  };

  const handleUpdate = async () => {
    if (!expanded || !sectionsSelected.length) return;

    setLoading(true);
    try {
      selectedSectionsGroup
        ? await deleteSections(selectedUser?.id, expanded, sectionsSelected)
        : await addSections(selectedUser?.id, expanded, sectionsSelected);

      toastAlert(
        `Secciones ${
          selectedSectionsGroup ? "eliminadas" : "agregadas"
        } con éxito`,
        "success"
      );
      await executeRefresh();
      onClose();
    } catch (error) {
      toastAlert("Error al actualizar secciones", "error");
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteDocument = async (documentId) => {
    if (!documentId || !selectedUser?.id) {
      toastAlert("No se puede eliminar: información incompleta", "error");
      return;
    }

    setLoading(true);
    try {
      await deleteDocument(documentId, selectedUser.id);
      toastAlert("Documento eliminado correctamente", "success");
      await executeRefresh();
      onClose();
    } catch (error) {
      toastAlert("Error al eliminar el documento", "error");
    } finally {
      setLoading(false);
    }
  };

  const assignedSections = getAssignedSections();
  const availableSections = getAvailableSections();
  const hasAssigned = assignedSections.length > 0;
  const hasAvailable = availableSections.length > 0;
  return (
    <Modal
      open={Boolean(open)}
      onClose={loading ? null : onClose}
      aria-labelledby="document-user-title"
      aria-describedby="document-user-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: 1200 },
          maxHeight: "90vh",
          bgcolor: "#f9fafb",
          boxShadow: 6,
          p: 4,
          borderRadius: 3,
          overflow: "auto",
        }}
      >
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 10,
              borderRadius: 3,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
          Documentos de {selectedUser?.user_name}
        </Typography>
        <Typography variant="body2" sx={{ mb: 5 }}>
          Lista de documentos asignados al usuario
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Divider sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Asignar un nuevo documento
              </Typography>
            </Divider>

            <DocumentAutocomplete
              token={token}
              documentID={documentSelected}
              setDocumentID={setDocumentSelected}
              assignedDocuments={[
                ...(selectedUser?.permissions || []),
                ...documents.map((doc) => ({ ID_DOCUMENTO: doc.document_id })),
              ]}
            />

            <Box sx={{ mt: 2, height: 450, overflowY: "auto" }}>
              {" "}
              {documents.map((doc) => (
                <Accordion
                  key={`new-doc-${doc.document_id}`}
                  expanded={expandedNewDoc === doc.document_id}
                  onChange={() =>
                    setExpandedNewDoc(
                      expandedNewDoc === doc.document_id
                        ? null
                        : doc.document_id
                    )
                  }
                  sx={{
                    mb: 1,
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: "#e5e5e5", px: 2, py: 0.5 }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocuments((prev) =>
                            prev.filter(
                              (d) => d.document_id !== doc.document_id
                            )
                          );
                        }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                      <Typography fontWeight={500}>{doc.name}</Typography>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 2, py: 2 }}>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Secciones disponibles
                      </Typography>
                    </Divider>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {doc.sections.map((section, i) => (
                        <Chip
                          key={`new-section-${doc.document_id}-${section.id}-${i}`}
                          label={section.name}
                          icon={<AddCircleRoundedIcon />}
                          variant={section.active ? "filled" : "outlined"}
                          color={section.active ? "primary" : "default"}
                          onClick={() =>
                            toggleSection(doc.document_id, section.id)
                          }
                          sx={{ fontSize: "12px", mb: 1 }}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
              {!documents.length && (
                <Typography
                  variant="body2"
                  sx={{ color: "#9ca3af", textAlign: "center", mt: 20 }}
                >
                  No hay documentos seleccionados para asignar.
                </Typography>
              )}
            </Box>

            {!!documents.length && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={loading}
                startIcon={
                  loading && <CircularProgress size={20} color="inherit" />
                }
              >
                {loading ? "Guardando..." : "Guardar asignación de documentos"}
              </Button>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Divider sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Documentos asignados
              </Typography>
            </Divider>{" "}
            <Box sx={{ height: 450, overflowY: "auto" }}>
              {selectedUser?.permissions?.length ? (
                selectedUser.permissions
                  .filter((permission) => permission && permission.ID_DOCUMENTO)
                  .map((permission, i) => (
                    <Accordion
                      key={`doc-${permission.ID_DOCUMENTO}-${i}`}
                      expanded={
                        !documents.length &&
                        expanded === permission.ID_DOCUMENTO
                      }
                      onChange={handleChange(permission.ID_DOCUMENTO)}
                      disabled={!!documents.length || loading}
                      sx={{
                        mb: 1,
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                        "&:before": { display: "none" },
                        opacity: documents.length || loading ? 0.5 : 1,
                        transition: "opacity 0.3s",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ bgcolor: "#e5e5e5", px: 2, py: 0.5 }}
                      >
                        {" "}
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              if (permission && permission.ID_DOCUMENTO) {
                                handleDeleteDocument(permission.ID_DOCUMENTO);
                              }
                            }}
                            size="small"
                            disabled={loading || !permission?.ID_DOCUMENTO}
                          >
                            <DeleteIcon />
                          </IconButton>
                          <Typography fontWeight={500}>
                            {permission?.NOMBRE_DOCUMENTO ||
                              "Documento sin nombre"}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2, py: 2 }}>
                        <>
                          <Divider sx={{ my: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Secciones asignadas
                            </Typography>
                          </Divider>

                          {hasAssigned ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                mb: 2,
                              }}
                            >
                              {assignedSections.map((section, i) => (
                                <Chip
                                  key={`assigned-${permission.ID_DOCUMENTO}-${section.section_id}-${i}`}
                                  label={section.section_name}
                                  onClick={() =>
                                    toggleSelection(section.section_id, true)
                                  }
                                  icon={<CancelRoundedIcon />}
                                  variant={
                                    sectionsSelected.includes(
                                      section.section_id
                                    )
                                      ? "filled"
                                      : "outlined"
                                  }
                                  color="primary"
                                  sx={{
                                    fontSize: "12px",
                                    mb: 1,
                                    opacity:
                                      !selectedSectionsGroup ||
                                      selectedSectionsGroup
                                        ? 1
                                        : 0.4,
                                    pointerEvents:
                                      !selectedSectionsGroup ||
                                      selectedSectionsGroup
                                        ? "auto"
                                        : "none",
                                  }}
                                />
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: "#9ca3af", mb: 2 }}
                            >
                              No hay secciones asignadas.
                            </Typography>
                          )}

                          <Divider sx={{ my: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Secciones sin asignar
                            </Typography>
                          </Divider>

                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            {hasAvailable ? (
                              availableSections.map((section, i) => (
                                <Chip
                                  key={`unassigned-${permission.ID_DOCUMENTO}-${section.section_id}-${i}`}
                                  label={section.section_name}
                                  onClick={() =>
                                    toggleSelection(section.section_id, false)
                                  }
                                  icon={<AddCircleRoundedIcon />}
                                  variant={
                                    sectionsSelected.includes(
                                      section.section_id
                                    )
                                      ? "filled"
                                      : "outlined"
                                  }
                                  color="success"
                                  sx={{
                                    fontSize: "12px",
                                    opacity:
                                      !selectedSectionsGroup ||
                                      !selectedSectionsGroup
                                        ? 1
                                        : 0.4,
                                    pointerEvents:
                                      !selectedSectionsGroup ||
                                      !selectedSectionsGroup
                                        ? "auto"
                                        : "none",
                                  }}
                                />
                              ))
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ color: "#9ca3af" }}
                              >
                                No hay secciones disponibles para asignar.
                              </Typography>
                            )}
                          </Box>

                          {!!sectionsSelected.length && expanded && (
                            <Button
                              variant="contained"
                              color={
                                selectedSectionsGroup ? "error" : "success"
                              }
                              fullWidth
                              sx={{ mt: 2 }}
                              onClick={handleUpdate}
                              disabled={loading}
                              startIcon={
                                loading && (
                                  <CircularProgress size={20} color="inherit" />
                                )
                              }
                            >
                              {loading
                                ? "Procesando..."
                                : selectedSectionsGroup
                                ? "Eliminar secciones seleccionadas"
                                : "Agregar secciones seleccionadas"}
                            </Button>
                          )}
                        </>
                      </AccordionDetails>
                    </Accordion>
                  ))
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#9ca3af", textAlign: "center", mt: 20 }}
                >
                  No hay documentos asignados a este usuario.
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};
